import { z } from 'zod';
/**
 * Organisation lifecycle state machine.
 *
 * States are validated — the API never accepts an arbitrary status string —
 * and every transition is recorded immutably in organisation_lifecycle_events.
 * Soft archive is intentionally NOT a lifecycle state: it is a separate
 * archived_at marker so that history and state semantics stay clean.
 */
export const LIFECYCLE_STATES = [
    'prospect',
    'evaluation',
    'onboarding',
    'live',
    'suspended',
    'churned',
];
export const LifecycleStateSchema = z.enum(LIFECYCLE_STATES);
export const LIFECYCLE_LABELS = {
    prospect: 'Prospect',
    evaluation: 'Evaluation',
    onboarding: 'Onboarding',
    live: 'Live',
    suspended: 'Suspended',
    churned: 'Churned',
};
/** Allowed transitions. Anything not listed here is rejected. */
export const LIFECYCLE_TRANSITIONS = {
    prospect: ['evaluation', 'churned'],
    evaluation: ['onboarding', 'churned'],
    onboarding: ['live', 'suspended', 'churned'],
    live: ['suspended', 'churned'],
    suspended: ['live', 'churned'],
    // Win-back: a churned organisation re-enters through evaluation, never
    // straight to live, so onboarding checks are not skipped.
    churned: ['evaluation'],
};
export function canTransition(from, to) {
    return (LIFECYCLE_TRANSITIONS[from] ?? []).includes(to);
}
export const LifecycleTransitionRequestSchema = z.object({
    toState: LifecycleStateSchema,
    /** Operator-supplied reason; required for every lifecycle change. */
    reason: z.string().trim().min(5).max(1000),
    /** Optimistic-concurrency guard: version of the organisation being changed. */
    expectedVersion: z.number().int().min(1),
});
export const LifecycleEventSchema = z.object({
    id: z.uuid(),
    organisationId: z.uuid(),
    fromState: LifecycleStateSchema.nullable(),
    toState: LifecycleStateSchema,
    reason: z.string(),
    actorUserId: z.uuid().nullable(),
    actorDisplayName: z.string().nullable(),
    occurredAt: z.iso.datetime(),
});
//# sourceMappingURL=lifecycle.js.map